import {useCallback, useEffect, useRef, useState} from "react";
import auth from "../../../utils/authentication";
import {searchPage} from "../api/cap17drive-api";
import {normalize} from "../../../utils/utils";
import useSearchStore from "../cap17driveSearcher/search-store";
import {UserLocalStorageApi} from "../../../apis/local-storage/UserLocalStorageApi";

export class SquadflixLocalSearchHistoryStore {
    static lastHistoryLoad: string[] = [];

    static async getSearchHistory(): Promise<string[]> {
        try {
            const history = await UserLocalStorageApi.getItem({key: "squadflix-search-history"});
            if (history.item && typeof history.item === "object" && Array.isArray(history.item)) {
                this.lastHistoryLoad = history.item;
                return history.item;
            }
            return [];
        } catch (e) {
            console.error("Error getting search history:", e);
            return [];
        }
    }

    static async filterSearchHistory(term: string): Promise<string[]> {
        if (!this.lastHistoryLoad || this.lastHistoryLoad.length === 0) {
            await this.getSearchHistory();
        }
        const lowerTerm = term.toLowerCase();
        return this.lastHistoryLoad.filter(t => normalize(t).toLowerCase().includes(normalize(lowerTerm)));
    }

    static async addSearchTerm(term: string): Promise<void> {
        try {
            if (!term || term.trim() === "") {
                return;
            }
            let history = await SquadflixLocalSearchHistoryStore.getSearchHistory();
            history = history.filter(t => t !== term);
            history.unshift(term);
            if (history.length > 50) {
                history = history.slice(0, 50);
            }
            this.lastHistoryLoad = history;
            await UserLocalStorageApi.setItem({key: "squadflix-search-history", value: history});
        } catch (e) {
            console.error("Error adding search term to history:", e);
        }
    }

    static clearHistory(): void {
        this.lastHistoryLoad = [];
        UserLocalStorageApi.removeItem({key: "squadflix-search-history"}).catch(e => {
            console.error("Error clearing search history:", e);
        });
    }
}

export const useSquadflixSearchData = (options?: {autoLoadLastSearch?: boolean}) => {
    const nameSearch = useSearchStore(state => state.nameSearch);
    const setNameSearch = useSearchStore(state => state.setNameSearch);
    const currentSearch = useSearchStore(state => state.currentSearch);
    const setCurrentSearch = useSearchStore(state => state.setCurrentSearch);
    const loading = useSearchStore(state => state.loading);
    const setLoading = useSearchStore(state => state.setLoading);
    const [searchHistory, setSearchHistory] = useState<string[]>([]);
    const lastSearchDone = useRef(false);

    const refreshHistory = useCallback(async () => {
        const list = await SquadflixLocalSearchHistoryStore.getSearchHistory();
        setSearchHistory(list);
    }, []);

    const makeSearch = useCallback(async (search?: string, saveToHistory = false) => {
        if (!auth.currentUser) return;
        const useSearch = (search ?? nameSearch ?? "").trim();
        if (!useSearch) return;

        if (currentSearch?.keywords !== useSearch) {
            setLoading(true);
            const [moviesData, seriesData] = await Promise.all([
                searchPage(useSearch, 1, "movies"),
                searchPage(useSearch, 1, "series"),
            ]);
            const data = {
                keywords: useSearch,
                movies: moviesData?.results || [],
                series: seriesData?.results || []
            };
            setCurrentSearch(data);
            localStorage.setItem("squadflix-last-search", useSearch);
            if (saveToHistory) {
                await SquadflixLocalSearchHistoryStore.addSearchTerm(useSearch);
                refreshHistory();
            }
            setLoading(false);
        } else if (saveToHistory) {
            await SquadflixLocalSearchHistoryStore.addSearchTerm(useSearch);
            refreshHistory();
        }
    }, [nameSearch, currentSearch?.keywords, setLoading, setCurrentSearch, refreshHistory]);

    const clearHistory = useCallback(() => {
        SquadflixLocalSearchHistoryStore.clearHistory();
        setSearchHistory([]);
    }, []);

    useEffect(() => {
        refreshHistory();
    }, [refreshHistory]);

    useEffect(() => {
        if (!options?.autoLoadLastSearch || lastSearchDone.current) return;
        lastSearchDone.current = true;
        const lastSearch = localStorage.getItem("squadflix-last-search");
        if (lastSearch) {
            setNameSearch(lastSearch);
            makeSearch(lastSearch, false);
        }
    }, [makeSearch, options?.autoLoadLastSearch, setNameSearch]);

    return {
        nameSearch,
        setNameSearch,
        currentSearch,
        loading,
        searchHistory,
        refreshHistory,
        clearHistory,
        makeSearch,
    };
};

export default useSquadflixSearchData;
